import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, reaction, runInAction, toJS } from "mobx";
import { fromPromise } from "mobx-utils";
import { modalService } from "@whatslly/ui-components/modals";
import { ComponentMessageType } from "@whatsllyorg/crm-components/ChatComponent";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { TuvisMessagingPhone, SelectedPhone } from "@whatslly/types/tuvis-messaging/TuvisMessagingPhones";
import { ContactOptInModal } from "../ContactOptInModal";
import { ArchiveItemModal } from "../ArchiveItemModal";
import type { ITuvisMessagingProvider } from "@whatslly/ui-components/Pages/TuvisMessaging/TuvisMessagingProvider";
import { TuvisMessagingProviderSymbol } from "@whatslly/ui-components/Pages/TuvisMessaging/TuvisMessagingProvider";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { DOM_EVENT_TYPES, IListenerServiceSymbol } from "@whatslly/types/instant-messaging/ListenerService";
import { ImChat } from "@whatslly/types/tuvis-messaging/TuvisMessagingChats";
import {
  ConversationStatus,
  CONVERSATION_STATUS,
  AssignmentFilter,
  ASSIGNMENT_FILTER,
} from "@whatslly/types/tuvis-messaging/TuvisMessagingConversations";
import { AssignableAgent } from "@whatslly/types/tuvis-messaging/TuvisMessagingConversations";
import { ALL_ACCEPT_FILE_TYPES_SIZE_MAP, DOCUMENT_SIZE_LIMIT_BYTES } from "@whatslly/ui-components/Pages/TuvisMessaging/agentUtils";
import type { ImContact } from "@whatslly/types/tuvis-messaging/TuvisMessagingContacts";
import { ALREADY_EXISTS_ERROR } from "@whatslly/types/tuvis-messaging/TuvisMessagingContacts";
import { ContactDetailsModal } from "@whatslly/ui-components/Pages/TuvisMessaging/ContactDetailsModal";
import ms from "ms";
import { SelectContactModal } from "@whatslly/ui-components/Pages/TuvisMessaging/SelectContactModal";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { AddChatToContactsModal } from "@whatslly/ui-components/Pages/TuvisMessaging/AddChatToContactsModal/AddChatToContactsModal";
import { MessagingEventBus } from "./MessagingEventBus";
import { contentMessagingClient } from "@whatslly/commons/contentMessagingClient";
import { ContentCommands } from "@whatslly/commons/contentCommands";
import { calFileSizeV2, stripNumberStringToOnlyNumbers, getPhoneCountryCode } from "@whatslly/commons/utils";
import i18n from "../i18n/config";
import type { ContactFilterObjectType } from "@whatslly/types/ContactFilterObject";
import { container } from "./container";
import { BlockMessagingService } from "./blockMessagingService";
import { InjectBlockMessaging } from "@whatslly/instant-messaging/blockMessaging";
import { Recorder } from "../AgentCamera/Recorder";
import { EmojiPickerModal } from "@whatslly/ui-components/Pages/TuvisMessaging/EmojiPickerModal";
import { TuvisContactsStore } from "./TuvisContactsStore";
import { TuvisMessagingNotificationsService, serviceWorkerBackend } from "./TuvisMessagingNotificationsService";
import type { TemplateLanguages } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { TemplatePurpose } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { OptInTemplate, OptInResult, HeaderMedia } from "@whatslly/types/waba/WhatsappBusinessApiSelectChat";
import { IMChat } from "./IMChat";
import { TuvisMessagingDataStore } from "./TuvisMessagingDataStore";
import { MessagingCommands } from "@whatslly/commons/tuvis-messaging/messagingCommands";
import { SWCommands } from "../../../services/serviceWorker/cmds";
import { ChatFromModal } from "@whatslly/ui-components/Pages/TuvisMessaging/ChatFromModal";
import { WhatsappBusinessApiPhone } from "@whatslly/types/waba/WhatsappBusinessApiGetPhones";
import { TwilioPhone } from "@whatslly/types/twilio/TwilioGetPhones";
import { IMessagePhone } from "@whatslly/types/imessage/IMessagePhone";
import { AddContactViaCSVModal } from "@whatslly/ui-components/Pages/TuvisMessaging/AddContactViaCSVModal";
import { ChatAddLabelsModal } from "@whatslly/ui-components/Pages/TuvisMessaging/ChatAddLabelsModal";
import uniq from "lodash/uniq";
import { InteractiveChannelType, isInteractiveChannel } from "@whatslly/types/tuvis-messaging/TuvisMessagingChannels";
import { OutboundMessageType } from "@whatslly/types/policy/enums";

type ChatsMode = "active" | "archived" | "unknown";

const channelType: Record<InteractiveChannelType, string> = {
  imessage: "local",
  twilio: "ws",
  whatsapp: "ws",
};
export const parseOrReturnError = async <T>(response: Response, logger: FrontLogger) => {
  try {
    if (response.ok) {
      return response.json() as Promise<T>;
    } else if (response.headers.get("content-type")?.includes("application/json")) {
      const { error } = await response.json();
      throw new Error(`${error ?? "Unknown error"}: ${response.status}`);
    }
    throw new Error(`Unknown error: ${response.status}`);
  } catch (error) {
    logger.error({ err: error }, "Error on whatsapp agent request");
    throw error;
  }
};

type SelectModeTypes = "labels" | "templates" | undefined;

@injectable()
export class TuvisMessagingStore {
  public chatsMode: ChatsMode = "active";
  focusMessageId = "";
  private _messagesSearchTerm = "";
  private _chatsListSearchTerm = "";
  private _showScrollDown = false;
  private _selectedChatId: string | undefined;
  private _textInputValue = "";
  public isInitialized = false;
  public initializing = false;
  private _phones: TuvisMessagingPhone[] = [];
  private _templates: TemplateLanguages[] = [];
  public sendOptInTemplate = false;
  public selectedNavMenuTab = "chats";
  public isChatNavChangedLoading = false;
  private abortController = new AbortController();
  public chatFilters: Pick<ContactFilterObjectType, "hasUnread" | "isLastMsgFromMe" | "phone" | "countryCode" | "imLabels"> = {};
  private _pendingUploads = new Map<string, File[]>();
  mediaStream: MediaStream | undefined;
  mediaRecorder: Recorder | undefined;
  private _currentQuotedMessageId?: string;
  public selectedChats = new Set<string>();

  public selectMode: SelectModeTypes = undefined;
  private _isReSendingIds: Set<string> = new Set();

  private _assignmentFilter: AssignmentFilter = ASSIGNMENT_FILTER.ALL;
  private _statusFilter: ConversationStatus | undefined = CONVERSATION_STATUS.OPEN;
  private _assignableAgentsByPhone: Map<string, AssignableAgent[]> = new Map();
  public hideBulkMessages: boolean = false;

  constructor(
    @inject(FrontLogger) private readonly logger: FrontLogger,
    @inject(TuvisMessagingProviderSymbol) private readonly dataProvider: ITuvisMessagingProvider,
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(IListenerServiceSymbol) private readonly messagingEventBus: MessagingEventBus,
    @inject(TuvisContactsStore) private readonly contactsStore: TuvisContactsStore,
    @inject(TuvisMessagingNotificationsService) private readonly notificationsService: TuvisMessagingNotificationsService,
    @inject(TuvisMessagingDataStore) private readonly dataStore: TuvisMessagingDataStore
  ) {
    makeObservable<
      TuvisMessagingStore,
      | "_chatsListSearchTerm"
      | "_selectedChatId"
      | "_messagesSearchTerm"
      | "_showScrollDown"
      | "_textInputValue"
      | "_pendingUploads"
      | "_currentQuotedMessageId"
      | "selectedPhone"
      | "activeChats"
      | "_isReSendingIds"
      | "editIsReSendingIds"
      | "_assignmentFilter"
      | "_statusFilter"
      | "_assignableAgentsByPhone"
      | "allChats"
    >(this, {
      chatsMode: observable,
      mediaStream: observable.ref,
      mediaRecorder: observable.ref,
      isChatNavChangedLoading: observable,
      selectedNavMenuTab: observable,
      sendOptInTemplate: observable,
      hasPhones: computed,
      hasTemplates: computed,
      optInTemplates: computed,
      hasSmartersPhoneWithoutToken: computed,
      focusMessageId: observable,
      setFocusMessageId: action,
      _chatsListSearchTerm: observable,
      _selectedChatId: observable,
      selectedChat: computed,
      _messagesSearchTerm: observable,
      _showScrollDown: observable,
      _textInputValue: observable,
      isInitialized: observable,
      chatFilters: observable,
      _pendingUploads: observable,
      _currentQuotedMessageId: observable,
      pendingUploads: computed,
      messagesSearchTerm: computed,
      chatsListSearchTerm: computed,
      showScrollDown: computed,
      updateSelectedChat: action,
      sendMessage: action.bound,
      textInputValue: computed,
      orgId: computed,
      handleUnreadMessages: action,
      viewMessages: computed,
      selectedChatId: computed,
      selectedPhone: computed,
      capabilities: computed,
      selectedPhoneParams: computed,
      currentQuotedMessage: computed,
      init: action,
      setNavMenuTab: action,
      chats: computed,
      unreadCounters: computed,
      activeChats: computed,
      archivedChats: computed,
      unknownChats: computed,
      filteredChats: computed,
      addPendingUpload: action,
      removePendingUpload: action.bound,
      clearPendingUploads: action.bound,
      stopCaptureMedia: action.bound,
      setCurrentQuotedMessageId: action,
      changeChatsMode: action,
      selectedChats: observable,
      addSelectedChatsList: action,
      clearSelectedChatsList: action,
      removeSelectedChatsList: action,
      showSendTemplateToSelectedChatsButton: computed,
      selectMode: observable,
      isSelectMode: computed,
      _isReSendingIds: observable,
      editIsReSendingIds: action,
      reSendingIds: computed,
      selectAllChatsList: action,
      labels: computed,
      isOnNewAgentMode: computed,
      _assignmentFilter: observable,
      _statusFilter: observable,
      assignmentFilter: computed,
      statusFilter: computed,
      _assignableAgentsByPhone: observable,
      hideBulkMessages: observable,
      setHideBulkMessages: action,
      allChats: computed,
    });
    serviceWorkerBackend.on(SWCommands.OPEN_CHAT, async (chatId) => {
      const chat = this.dataStore.chats.get(chatId);
      if (chat) this.updateSelectedChat(chat);
    });

    reaction(
      () => this.chatFilters,
      () => this.clearSelectedChatsList()
    );
  }

  get isOnNewAgentMode() {
    return this.userStore.user?.new_agent_mode ?? false;
  }

  setHideBulkMessages(value: boolean) {
    this.hideBulkMessages = value;
  }

  changeChatsMode(mode: ChatsMode) {
    this.chatsMode = mode;
    this._chatsListSearchTerm = "";
    this.clearSelectedChatsList();
    this.removeSelectedChat();
  }

  setCurrentQuotedMessageId(messageId: string | undefined) {
    this._currentQuotedMessageId = messageId;
  }

  get currentQuotedMessage() {
    if (!this._currentQuotedMessageId) return undefined;
    return this.viewMessages.find((i) => i.msgId === this._currentQuotedMessageId);
  }

  removeSelectedChat() {
    this._selectedChatId = undefined;
    this._textInputValue = "";
    this._messagesSearchTerm = "";
    this.focusMessageId = "";
    this.sendOptInTemplate = false;
    this.messagingEventBus.emit(DOM_EVENT_TYPES.CONTACT_CHANGED);
    this.stopCaptureMedia();
  }

  private get selectedPhone() {
    return this.getPhoneByChat(this.selectedChat);
  }

  get hasPhones() {
    return this._phones.length > 0;
  }

  get hasTemplates() {
    return this.userStore.user?.channels?.waba?.length ? this._templates.length > 0 : true;
  }

  get optInTemplates() {
    return this._templates.filter((t) => (t.purposes ?? []).length === 0 || t.purposes!.includes(TemplatePurpose.OPT_IN));
  }

  get hasSmartersPhoneWithoutToken() {
    return this._phones.some((phone) => phone.type === "whatsapp" && phone.isSmarters && !phone.hasSmartersToken);
  }

  get selectedPhoneParams(): SelectedPhone | undefined {
    if (!this.selectedPhone) return;
    switch (this.selectedPhone.type) {
      case "whatsapp":
        return {
          type: "whatsapp",
          phoneNumberId: this.selectedPhone.id,
          phoneNumber: this.selectedPhone.phone,
          wabaId: this.selectedPhone.wabaId,
        };
      case "twilio":
        return {
          type: "twilio",
          phoneNumberId: this.selectedPhone.id,
          phoneNumber: this.selectedPhone.phone,
          accountSid: this.selectedPhone.accountSid,
        };
      case "imessage":
        return {
          type: "imessage",
          phoneNumberId: this.selectedPhone.id,
          phoneNumber: this.selectedPhone.phone,
        };
    }
  }

  get capabilities() {
    if (!this.selectedPhone) return;
    switch (this.selectedPhone.type) {
      case "whatsapp":
        return { textMessage: true, mediaMessage: true, messageReaction: true, messageReply: true };
      case "imessage":
        return { textMessage: true, mediaMessage: true, messageReaction: true, messageReply: true };
      case "twilio":
        return { textMessage: true, mediaMessage: Boolean(this.selectedPhone.capabilities.mms), messageReaction: false, messageReply: false };
    }
  }

  setFocusMessageId(id: string) {
    this.focusMessageId = id;
  }

  async setNavMenuTab(tab: string, clearSelectedChat = true) {
    if (this.selectedNavMenuTab === tab) {
      return;
    }
    this.isChatNavChangedLoading = true;
    if (clearSelectedChat) {
      this.removeSelectedChat();
    }
    this.contactsStore.removeSelectedContact();
    this.abortController.abort();
    this.selectedNavMenuTab = tab;
    runInAction(() => {
      this.isChatNavChangedLoading = false;
    });
  }

  get phone() {
    if (!this.selectedPhone) return stripNumberStringToOnlyNumbers(this._phones[0]?.phone ?? "");
    return stripNumberStringToOnlyNumbers(this.selectedPhone.phone);
  }

  get wsClient() {
    return this.dataStore.wsClient;
  }

  getPhoneByChat(chat?: ImChat) {
    return this._phones.find((phone) => phone.id === chat?.phoneId);
  }

  get chatsListSearchTerm() {
    return this._chatsListSearchTerm;
  }

  set chatsListSearchTerm(newSearchTerm) {
    this._chatsListSearchTerm = newSearchTerm;
  }

  get messagesSearchTerm() {
    return this._messagesSearchTerm;
  }

  set messagesSearchTerm(newSearchTerm) {
    this._messagesSearchTerm = newSearchTerm;
  }

  get showScrollDown() {
    return this._showScrollDown;
  }
  set showScrollDown(show: boolean) {
    this._showScrollDown = show;
  }

  get orgId() {
    return fromPromise(this.dataProvider.getOrgId());
  }

  get viewMessages() {
    return (this.selectedChat?.messages ?? []).map((message) => {
      const currentMessage: ComponentMessageType = {
        senderName: "",
        id: message._id,
        status: message.status,
        errorCode: message.errorCode,
        isSent: message.isSent,
        msg: message.message ?? "",
        msgId: message.messageId,
        datetime: message.createdAt,
        senderPhoneNumber: `${message.senderNumber}`,
        receiverPhoneNumber: `${message.recipientNumber}`,
        quotedMsgId: message.quotedMsgId,
        file: message.file,
        location: message.location,
        contacts: message.contacts,
        reactions: message.reactions,
        height: message.height,
        width: message.width,
        isTemplate: message.type === "template",
        templateName: message.templateName,
        isInternal: (message as any).isInternal ?? false,
      };
      return currentMessage;
    });
  }

  async markMessagesAsRead(msgIds: string[]) {
    if (!this.selectedPhoneParams) {
      return;
    }
    try {
      await this.dataStore.wsClient.send(MessagingCommands.MARKE_MESSAGE_AS_READ, { selectedPhone: this.selectedPhoneParams, msgIds });
    } catch (err) {
      this.logger.error({ err }, "failed to mark messages as read");
    }
  }

  private async selectChat(
    msgIds: string[],
    chatId: string,
    optInTemplate?: OptInTemplate,
    assignConversationToMe?: boolean,
    setConversationToOpen?: boolean,
    headerMedia?: HeaderMedia
  ) {
    if (!this.selectedPhoneParams) {
      return;
    }
    try {
      await Promise.all([
        this.dataStore.wsClient.send(MessagingCommands.SELECT_CHAT, {
          selectedPhone: this.selectedPhoneParams,
          _id: chatId,
          optInTemplate,
          assignConversationToMe,
          setConversationToOpen,
          headerMedia,
        }),
        this.markMessagesAsRead(msgIds),
      ]);
    } catch (err: any) {
      modalService.openModal(Toast, { text: err, type: "error" });
      this.logger.error({ err }, "failed to update on select chat");
    }
  }

  async init() {
    if (this.initializing) {
      return;
    }
    this.initializing = true;
    await this.dataProvider.init?.();
    await this.dataStore.init();
    const [phoneRes, templatesRes] = await Promise.all([
      this.dataStore.wsClient.send(MessagingCommands.GET_USER_PHONES),
      this.dataStore.wsClient.send(MessagingCommands.GET_TEMPLATES),
    ]);
    this.notificationsService.init().catch((err) => this.logger.error({ err }, "failed to init notifications service"));
    let phones: Array<WhatsappBusinessApiPhone | TwilioPhone | IMessagePhone> | undefined;
    try {
      phones = await this.dataProvider.getPhones?.();
    } catch (e) {
      // nope
    }
    runInAction(() => {
      this._phones = [...phoneRes.phones, ...(phones ?? [])];
      this._templates = templatesRes.templates;
      this.isInitialized = true;
    });

    const data = {
      active: this.isInitialized,
      loggedIn: !!this.dataProvider.etoken,
      pod: this.dataProvider.pod,
      loginProvider: this.userStore.loginProvider,
      user: toJS(this.userStore.user),
      extensionVersion: "",
      darkMode: window.matchMedia("(prefers-color-scheme: dark)").matches,
    };
    contentMessagingClient.send(ContentCommands.AUTH, data);
    contentMessagingClient.send(ContentCommands.CONTENT_READY);
  }

  get chats() {
    if (this.chatsMode === "archived") {
      return this.archivedChats;
    }
    if (this.isOnNewAgentMode) {
      return this.allChats.filter((chat) => !chat.isArchived);
    }
    if (this.chatsMode === "unknown") {
      return this.unknownChats;
    }
    return this.activeChats;
  }

  get phones() {
    return this._phones;
  }

  get templates() {
    return this._templates;
  }

  private get allChats() {
    return this.isInitialized ? this.dataStore.chats : [];
  }

  private get activeChats() {
    return this.allChats.filter((chat) => !chat.isArchived && chat.createdBy === "user");
  }

  get archivedChats() {
    return this.allChats.filter((chat) => chat.isArchived);
  }

  get unknownChats() {
    if (this.isOnNewAgentMode) {
      return [];
    }
    return this.allChats.filter((chat) => !chat.isArchived && chat.createdBy === "webhook");
  }

  handleUnreadMessages() {
    // TODO: do this only in case document.visibilityState === "visible"
    if (this.selectedChat && this.selectedChat.unreadMessages.length > 0) {
      const messageIds = this.selectedChat.unreadMessages.map((msg) => msg.messageId).filter((id) => id !== undefined);
      this.markMessagesAsRead(messageIds);
      this.selectedChat.markAsRead();
    }
  }

  get unreadCounters() {
    const counters = { chats: 0, messages: 0 };
    this.chats.forEach((chat) => {
      if (this.isOnNewAgentMode) {
        const status = chat.currentConversation?.status;
        if (status === CONVERSATION_STATUS.BOT || !status) return;
      }
      counters.chats += chat.unreadMessages.length ? 1 : 0;
      counters.messages += chat.unreadMessages.length;
    });
    return counters;
  }

  get filteredChats() {
    const term = this.chatsListSearchTerm.trim().toLowerCase();
    const currentUserId = this.userStore.user?._id;
    return this.chats
      .filter((chat) => {
        const lastMsg = chat.messages.at(-1);
        const chatLabelsAsSet = new Set(chat.labels || []);
        if (!chat.displayName?.toLowerCase().includes(term)) return false;
        if (this.chatFilters.hasUnread && chat.unreadMessages.length === 0) return false;
        if (this.chatFilters.isLastMsgFromMe === true && (!chat.messages.length || lastMsg?.isSent === false)) return false;
        if (this.chatFilters.isLastMsgFromMe === false && (!chat.messages.length || lastMsg?.isSent === true)) return false;
        if (this.chatFilters.phone && !this.chatFilters.phone.includes(chat.phone.toString())) return false;
        if (
          this.chatFilters.imLabels &&
          this.chatFilters.imLabels.length > 0 &&
          !this.chatFilters.imLabels.some((label) => chatLabelsAsSet.has(label))
        )
          return false;
        if (this.chatFilters.countryCode?.length && !this.chatFilters.countryCode.includes(getPhoneCountryCode(chat.phone.toString()) ?? ""))
          return false;
        // Assignment + status filters only when new agent mode (filter UI is shown); otherwise show all chats
        if (!this.isOnNewAgentMode) return true;
        const conversation = chat.currentConversation;
        const assignedAgentIds = conversation?.assignedAgentIds ?? [];
        if (this.assignmentFilter === "unassigned" && assignedAgentIds.length > 0) return false;
        if (this.assignmentFilter === "assigned_to_me" && (!currentUserId || !assignedAgentIds.includes(currentUserId))) return false;
        const chatConversationStatus = conversation?.status ?? CONVERSATION_STATUS.OPEN;
        if (chatConversationStatus === CONVERSATION_STATUS.BOT) return false;
        if (this.statusFilter && chatConversationStatus !== this.statusFilter) return false;
        return true;
      })
      .toSorted((a, b) => b.lastDate.valueOf() - a.lastDate.valueOf());
  }

  async sendReaction(msgId: string, emoji: string) {
    if (!this.selectedPhoneParams || !this.selectedChat) {
      return;
    }
    await this.dataStore.wsClient.send(MessagingCommands.SEND_MESSAGE_REACTION, {
      selectedPhone: this.selectedPhoneParams,
      recipient: this.selectedChat.phone,
      emoji,
      msgId,
    });
  }

  async openEmojiPickerModal(msgId: string) {
    await modalService.openModal(EmojiPickerModal, {
      onEmojiSelect: (emo) => this.sendReaction(msgId, emo.native),
    }).promise;
  }

  async openChatOptInModalAndSendSelectToServer(chat: IMChat | undefined) {
    if (!chat) {
      return false;
    }
    let optInResult: OptInResult | undefined = undefined;
    if (!chat?.isOptIn && !chat.isTemplateSent) {
      const phone = this.getPhoneByChat(chat);
      const templates = phone?.type === "whatsapp" ? this.optInTemplates : [];
      optInResult = await modalService.openModal(ContactOptInModal, { templates, numberOfSelectedChats: Number(this.selectedChats.size) }).promise;
    }
    runInAction(() => {
      this.sendOptInTemplate = Boolean(optInResult);
      this._selectedChatId = chat._id;
    });
    await this.selectChat(
      chat.unreadMessages.map((msg) => msg.messageId).filter((id) => id !== undefined),
      chat._id,
      optInResult?.template,
      optInResult?.assignConversationToMe,
      optInResult?.setConversationToOpen,
      optInResult?.headerMedia
    );
  }

  async updateSelectedChat(chat: IMChat) {
    if (this._selectedChatId === chat._id) {
      return;
    }
    runInAction(() => {
      this.sendOptInTemplate = false;
      this._selectedChatId = chat._id;
      this._textInputValue = "";
      this._messagesSearchTerm = "";
      this.focusMessageId = "";
    });
    await this.selectChat(
      chat.unreadMessages.map((msg) => msg.messageId).filter((id) => id !== undefined),
      chat._id
    );
    runInAction(() => {
      chat.markAsRead();
    });
    this.stopCaptureMedia();
    this.messagingEventBus.emit(DOM_EVENT_TYPES.CONTACT_CHANGED);
  }

  async unarchiveChat(chat: IMChat) {
    const res = await modalService.openModal(ConfirmModal, {
      testId: "waba_unarchive_modal",
      closeOnClickOutside: false,
      closeOnEscape: false,
      body: `${i18n.t("whatsapp_business_api_agent_page.archive_chat.unarchive_message", { name: chat.displayName })}`,
    }).promise;
    if (!res?.result) {
      return;
    }

    try {
      await this.dataStore.wsClient.send(MessagingCommands.UNARCHIVE_CHAT, { _id: chat._id });
    } catch (e: any) {
      modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.failed_to_unarchive_chat"), type: "error" });
    }
  }

  async archiveChat(chat: IMChat) {
    const res = await modalService.openModal(ArchiveItemModal, { type: "archive_chat", name: chat.displayName }).promise;
    if (!res?.result) {
      return;
    }

    try {
      await this.dataStore.wsClient.send(MessagingCommands.ARCHIVE_CHAT, { _id: chat._id });
    } catch (e: any) {
      modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.failed_to_archive_chat"), type: "error" });
    }
  }

  get textInputValue() {
    return this._textInputValue;
  }

  set textInputValue(text: string) {
    this._textInputValue = text;
  }

  get selectedChatId() {
    return this._selectedChatId;
  }

  get selectedChat() {
    if (!this._selectedChatId) {
      return;
    }
    return this.dataStore.chats.get(this._selectedChatId);
  }

  async selectPhoneNumberAndAddChat(contactId: string) {
    const phoneResult =
      this._phones.length > 1 ? await modalService.openModal(ChatFromModal, { phones: this._phones }).promise : { phone: this._phones[0] };
    if (!phoneResult?.phone) {
      return;
    }
    const chat = this.dataStore.chats.find((chat) => chat.contactId === contactId && chat.phoneId === phoneResult.phone.id);
    if (chat) {
      await this.updateSelectedChat(chat);
      return;
    }
    const contact = this.contactsStore.contacts.get(contactId);
    if (!contact) {
      return;
    }
    await this.createNewChat(contact, phoneResult.phone);
  }

  async addChat() {
    const result = await modalService.openModal(SelectContactModal).promise;
    if (!result) {
      return;
    }
    await this.selectPhoneNumberAndAddChat(result.contactId);
  }

  async addContactsViaCsv() {
    const result = await modalService.openModal(AddContactViaCSVModal).promise;
    if (!result?.contacts) {
      return;
    }
    const res = await this.dataStore.wsClient.send(MessagingCommands.IMPORT_CONTACTS_FROM_CSV, result.contacts);
    if (!res.success) {
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.failed_to_import_contacts_from_csv"),
        type: "error",
        timeout: ms("30sec"),
      });
      return;
    }
    modalService.openModal(Toast, {
      text: i18n.t("whatsapp_business_api_agent_page.contacts_imported_successfully"),
      timeout: ms("30sec"),
    });
  }

  async createNewChat(contact: ImContact, _phone?: TuvisMessagingPhone) {
    const phone = _phone ?? this._phones[0];
    if (!phone) return;
    try {
      const res = await this.dataStore.wsClient.send(MessagingCommands.CREATE_CHAT, {
        contactId: contact._id,
        phone: contact.phones[0].phone,
        phoneId: phone?.id,
        type: phone.type,
      });
      await this.dataStore.chats.insert(res.chat);
      const chat = this.dataStore.chats.get(res.chat._id);
      if (chat) {
        await this.updateSelectedChat(chat);
      }
    } catch (e: any) {
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.failed_to_start_new_chat"),
        type: "error",
        timeout: ms("30sec"),
      });
      return;
    }
  }

  async addChatToContacts(chat: ImChat) {
    const res = await modalService.openModal(AddChatToContactsModal).promise;
    if (!res) {
      return;
    }
    const reqBody: {
      chatId: string;
      phone: number;
      contactId?: string;
      firstName?: string;
      lastName?: string;
    } = { chatId: chat._id, phone: chat.phone };
    if (res.type === "create") {
      const result = await modalService.openModal(ContactDetailsModal, {
        hidePhone: true,
        title: i18n.t("whatsapp_business_api_agent_page.add_contact_modal.add_contact_title"),
      }).promise;
      if (!result) {
        return;
      }
      reqBody.firstName = result.firstName;
      reqBody.lastName = result.lastName;
    }
    if (res.type === "assign") {
      const result = await modalService.openModal(SelectContactModal, { hideCreateButton: true }).promise;
      if (!result) {
        return;
      }
      reqBody.contactId = result.contactId;
    }

    try {
      await this.dataStore.wsClient.send(MessagingCommands.ADD_CHAT_TO_CONTACT, reqBody);
      this.clearSelectedChatsList();
    } catch (e: any) {
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.failed_to_add_contact_to_contacts"),
        type: "error",
        timeout: ms("30sec"),
      });
    }
  }

  async sendMediaMessages() {
    const { selectedPhoneParams, selectedChat } = this;
    const files = this.pendingUploads.slice();
    if (!files.length || !selectedPhoneParams || !selectedChat) {
      return;
    }
    try {
      const currentText = this._textInputValue.trim();
      const replyMessageId = this._currentQuotedMessageId ? String(this._currentQuotedMessageId) : undefined;
      const shouldBlock = await this.blockMessaging.scanTextMessage(currentText, undefined, OutboundMessageType.TEXT);
      if (shouldBlock) {
        return;
      }
      this.clearPendingUploads();
      runInAction(() => {
        this._textInputValue = "";
        this._currentQuotedMessageId = undefined;
      });

      await Promise.all(
        files.map(async (file, index) => {
          const shouldBlock = await this.blockMessaging.scanAttachmentMessage(file);
          if (shouldBlock) {
            this.addPendingUpload([file]);
            return;
          }
          const [{ width, height }, binary] = await Promise.all([
            new Promise<{ width?: number; height?: number }>((resolve) => {
              if (!file.type.startsWith("image/")) {
                return resolve({ width: undefined, height: undefined });
              }
              const img = new Image();
              img.onload = () => resolve({ width: img.width, height: img.height });
              img.onerror = () => resolve({ width: undefined, height: undefined });
              img.src = URL.createObjectURL(file);
            }),
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = (e) => reject(e);
              reader.readAsBinaryString(file);
            }),
          ]);
          await this.dataStore.wsClient.send(MessagingCommands.SEND_MEDIA_MESSAGE, {
            selectedPhone: selectedPhoneParams,
            text: index === 0 ? currentText : undefined,
            binary,
            mimeType: file.type,
            fileName: file.name,
            width,
            height,
            recipient: selectedChat.phone,
            replyMessageId,
            chatId: selectedChat._id,
            conversationId: selectedChat.currentConversation?._id,
          });
        })
      );
    } catch (err) {
      this.logger.error({ err }, "failed to send media message");
      modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.failed_to_send_message"), type: "error" });
    }
  }

  private editIsReSendingIds(action: "add" | "delete", msgId: string) {
    const newSet = new Set(this._isReSendingIds);
    if (action === "add") {
      newSet.add(msgId);
      this._isReSendingIds.add(msgId);
    } else {
      newSet.delete(msgId);
    }
    this._isReSendingIds = newSet;
  }

  get reSendingIds() {
    return Array.from(this._isReSendingIds);
  }

  async resendMessage(msgId: string | undefined) {
    if (!msgId) return;
    const userAccept = await modalService.openModal(ConfirmModal, {
      title: i18n.t("resend_message.resend_message"),
      body: i18n.t("resend_message.confirm_resend_message"),
    }).promise;
    if (!this.selectedPhoneParams || this.reSendingIds.includes(msgId) || !userAccept?.result) return;
    this.editIsReSendingIds("add", msgId);
    const res = await this.dataStore.wsClient
      .send(MessagingCommands.RE_SEND_MESSAGE, {
        msgId,
        selectedPhone: this.selectedPhoneParams,
      })
      .finally(() => {
        this.editIsReSendingIds("delete", msgId);
      });
    if (!res || !res.success) {
      modalService.openModal(Toast, { text: i18n.t(`resend_message.${res.error}`) });
    } else {
      modalService.openModal(Toast, { text: i18n.t("resend_message.message_resent") });
    }
  }

  #blockMessaging?: BlockMessagingService;
  private get blockMessaging() {
    // service is lazy loaded to avoid circular dependency
    if (!this.#blockMessaging) {
      this.#blockMessaging = container.get(InjectBlockMessaging);
    }
    return this.#blockMessaging;
  }

  async sendMessageAction(currentText: string) {
    try {
      if (!this.selectedPhoneParams || !this.selectedChat || !isInteractiveChannel(this.selectedChat.type)) {
        return;
      }
      const currentQuotedMessageId = this._currentQuotedMessageId ? String(this._currentQuotedMessageId) : undefined;
      switch (channelType[this.selectedChat.type]) {
        case "local":
          await this.dataProvider.sendMessage?.(`iMessage;-;+${this.selectedChat.phone}`, currentText);
          break;
        default:
          await this.dataStore.wsClient.send(MessagingCommands.SEND_TEXT_MESSAGE, {
            selectedPhone: this.selectedPhoneParams,
            text: currentText,
            recipient: this.selectedChat.phone,
            replyMessageId: currentQuotedMessageId,
            chatId: this.selectedChat._id,
            conversationId: this.selectedChat.currentConversation?._id,
          });
      }
    } catch (err) {
      this.logger.error({ err }, "failed to send message");
      modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.failed_to_send_message"), type: "error" });
    }
  }

  async sendMessage() {
    try {
      const currentText = this._textInputValue.trim();
      const shouldBlock = await this.blockMessaging.scanTextMessage(currentText, undefined, OutboundMessageType.TEXT);
      if (shouldBlock) {
        return;
      }
      runInAction(() => {
        this._textInputValue = "";
        this._currentQuotedMessageId = undefined;
      });
      this.sendMessageAction(currentText);
    } catch (err) {
      this.logger.error({ err }, "failed to send message");
      modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.failed_to_send_message"), type: "error" });
    }
  }

  get pendingUploads() {
    return this._pendingUploads.get(this._selectedChatId ?? "") ?? [];
  }
  clearPendingUploads() {
    if (!this._selectedChatId) return;
    this._pendingUploads.delete(this._selectedChatId);
  }
  removePendingUpload(index: number) {
    this.pendingUploads.splice(index, 1);
  }
  addPendingUpload(files: File[]) {
    const filesToRemove = files.filter((file) => {
      if (ALL_ACCEPT_FILE_TYPES_SIZE_MAP[file.type]) {
        return file.size > ALL_ACCEPT_FILE_TYPES_SIZE_MAP[file.type];
      }
      return file.size > DOCUMENT_SIZE_LIMIT_BYTES;
    });
    filesToRemove.forEach((file) => {
      const fileSizeLimit = ALL_ACCEPT_FILE_TYPES_SIZE_MAP[file.type];
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.file_exeeds_limit", {
          fileName: file.name,
          maxFileSize: ` ${calFileSizeV2({ size: fileSizeLimit } as File)}`,
        }),
        type: `error`,
        timeout: ms(`10sec`),
      });
    });
    const prev = this._pendingUploads.get(this._selectedChatId ?? "");
    this._pendingUploads.set(this._selectedChatId ?? "", [...(prev ?? []), ...files.filter((file) => !filesToRemove.includes(file))]);
  }

  stopCaptureMedia() {
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = undefined;
    this.mediaRecorder?.stop();
    this.mediaRecorder = undefined;
  }
  async captureImage() {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    runInAction(() => (this.mediaStream = stream));
  }
  async captureAudio() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    runInAction(() => (this.mediaRecorder = new Recorder(stream)));
  }
  async sendLocationMessage() {
    if (!this.selectedPhoneParams || !this.selectedChat) {
      return;
    }
    try {
      const location = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      if (!location?.coords) {
        return;
      }
      await this.dataStore.wsClient.send(MessagingCommands.SEND_LOCATION_MESSAGE, {
        selectedPhone: this.selectedPhoneParams,
        location: { latitude: location.coords.latitude, longitude: location.coords.longitude },
        recipient: this.selectedChat.phone,
        replyMessageId: this._currentQuotedMessageId,
        chatId: this.selectedChat._id,
        conversationId: this.selectedChat.currentConversation?._id,
      });
      this.setCurrentQuotedMessageId(undefined);
    } catch (err) {
      this.logger.error({ err }, "failed to get location");
    }
  }

  private getSelectedPhoneParams(selectedPhone = this.selectedPhone): SelectedPhone | undefined {
    if (!selectedPhone) return;
    switch (selectedPhone.type) {
      case "whatsapp":
        return {
          type: "whatsapp",
          phoneNumberId: selectedPhone.id,
          phoneNumber: selectedPhone.phone,
          wabaId: selectedPhone.wabaId,
        };
      case "twilio":
        return {
          type: "twilio",
          phoneNumberId: selectedPhone.id,
          phoneNumber: selectedPhone.phone,
          accountSid: selectedPhone.accountSid,
        };
    }
  }

  get showSendTemplateToSelectedChatsButton() {
    const noneWhatsappChats = Array.from(this.selectedChats.values())
      .map((chatId) => this.dataStore.chats.get(chatId))
      .map((chat) => this.getPhoneByChat(chat))
      .filter((phone) => phone?.type !== "whatsapp");
    return noneWhatsappChats.length === 0;
  }

  async sendTemplateToSelectedChats() {
    const chats = Array.from(this.selectedChats.values())
      .map((chatId) => this.dataStore.chats.get(chatId))
      .filter(Boolean) as IMChat[];

    if (!chats.length) return this.clearSelectedChatsList();
    const res = await modalService.openModal(ContactOptInModal, {
      templates: this.optInTemplates,
      disableOptIn: true,
      numberOfSelectedChats: Number(this.selectedChats.size),
    }).promise;
    if (!res) return;
    const chatsToSend = chats
      .map((chat) => {
        const chatAsSelectedPhone = this.getSelectedPhoneParams(this.getPhoneByChat(chat));
        if (!chatAsSelectedPhone) return;
        return {
          selectedPhone: chatAsSelectedPhone,
          _id: chat._id,
        };
      })
      .filter(Boolean) as { selectedPhone: SelectedPhone; _id: string }[];
    try {
      this.cancelSelectMode();
      const sendingModal = modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.send_templates_to_many.sendingTemplates"),
        timeout: 999999,
      });
      const chatsRes = await this.dataStore.wsClient.send(MessagingCommands.SEND_TEMPLATE_TO_SELECTED_CHATS, {
        chats: chatsToSend,
        optInTemplate: res.template,
        assignConversationToMe: res.assignConversationToMe,
        setConversationToOpen: res.setConversationToOpen,
        headerMedia: res.headerMedia,
      });
      sendingModal.close(undefined);
      const failedToSend = chatsRes.filter((chat) => !chat.success);
      const isFailed = chatsRes.length === 0 || chatsRes.length === failedToSend.length;
      if (isFailed) modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.send_templates_to_many.failedToSendTemplates") });
      else if (!failedToSend.length)
        modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.send_templates_to_many.templatesSent") });
      else {
        modalService.openModal(Toast, {
          text: i18n.t("whatsapp_business_api_agent_page.send_templates_to_many.failedToSendToChats", { numberOfFailed: failedToSend.length }),
        });
      }
    } catch (err) {
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.send_templates_to_many.failedToSendTemplates"),
      });
      this.logger.error({ err }, "Failed to send template to selected chats");
    }
  }

  async addLabelsToChats() {
    const chats = Array.from(this.selectedChats.values())
      .map((chatId) => this.dataStore.chats.get(chatId))
      .filter(Boolean) as IMChat[];
    if (!chats.length) return this.clearSelectedChatsList();

    const labels = await this.labels;

    const res = await modalService.openModal(ChatAddLabelsModal, {
      chats,
      numberOfSelectedChats: chats.length,
      labels: labels || {},
    }).promise;
    if (!res) return;

    const chatsIds = chats.map((chat) => chat._id).filter(Boolean);
    const handleFail = (err?: any) => {
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.labels.modal.failed_to_add_labels_to_selected_chats"),
      });
      this.logger.error(
        { err, chatsIds, labelsToRemove: res.labelsToRemove, labelsToAdd: res.labelsToAdd },
        "Failed to add labels to selected chats"
      );
    };

    try {
      const chatsRes = await this.dataStore.wsClient.send(MessagingCommands.MODIFY_LABEL_TO_SELECTED_CHATS, {
        chatsIds,
        labelsToAdd: res.labelsToAdd,
        labelsToRemove: res.labelsToRemove,
      });
      if (!chatsRes) return handleFail();

      for (const chat of chats) {
        const labelsToSet = (chat.labels || []).filter((label) => !res.labelsToRemove.includes(label));
        chat.labels = uniq([...labelsToSet, ...res.labelsToAdd]);
      }
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.labels.modal.succeed_to_add_labels_to_selected_chats"),
      });
      const forceRefreshChatsInUi = () => {
        const ChatsListSearchTermDefaultState = String(this.chatsListSearchTerm);
        this.chatsListSearchTerm = `${ChatsListSearchTermDefaultState} `;
        this.chatsListSearchTerm = ChatsListSearchTermDefaultState;
      };
      forceRefreshChatsInUi();
      return true;
    } catch (err) {
      handleFail(err);
    }
  }

  removeSelectedChatsList(chatId: string) {
    this.selectedChats.delete(chatId);
  }

  addSelectedChatsList(chatId: string) {
    this.selectedChats.add(chatId);
  }

  selectAllChatsList(selectAll: boolean) {
    if (!selectAll) {
      this.clearSelectedChatsList();
      return;
    }
    const WhatsappChats = this.filteredChats
      .filter((chat) => {
        const chatId = chat._id;
        const dataStoreChat = this.dataStore.chats.get(chatId);
        const chatPhone = this.getPhoneByChat(dataStoreChat);
        return chatPhone?.type === "whatsapp";
      })
      .map((chat) => chat._id);
    this.selectedChats = new Set(WhatsappChats);
  }

  clearSelectedChatsList() {
    this.selectedChats.clear();
  }

  get isSelectMode() {
    return Boolean(this.selectMode);
  }

  cancelSelectMode() {
    this.clearSelectedChatsList();
    this.selectMode = undefined;
  }

  selectModeInit(selectMode: SelectModeTypes) {
    this.clearSelectedChatsList();
    this.selectMode = selectMode;
  }

  async createNewLabel(labelName: string) {
    try {
      const res = await this.dataStore.wsClient.send(MessagingCommands.CREATE_LABEL, {
        labelName,
      });
      await this.dataStore.labels.insert(res.imLabel);
    } catch (err: any) {
      if (err === ALREADY_EXISTS_ERROR) {
        modalService.openModal(Toast, { text: i18n.t("top_bar.labels_filter.label_name_is_duplicated"), type: "error", timeout: ms("30sec") });
      }
      modalService.openModal(Toast, {
        text: i18n.t("top_bar.labels_filter.failed_to_add_messaging_label"),
        type: "error",
        timeout: ms("30sec"),
      });
    }
  }

  async deleteLabel(labelId: string) {
    const res = await this.dataStore.wsClient.send(MessagingCommands.DELETE_LABEL, {
      _id: labelId,
    });
    if (!res.success) return false;
    this.dataStore.labels.remove(labelId);
    return true;
  }

  get labels() {
    const imLabelsDataCollection = this.isInitialized ? this.dataStore.labels : undefined;
    const getLabelsAsRecords = async () => {
      const labelsAsRecord: Record<string, string> = {};
      if (!imLabelsDataCollection) return labelsAsRecord;
      for await (const label of imLabelsDataCollection.items.values()) {
        if (label.isDeleted) {
          continue;
        }
        labelsAsRecord[label._id] = label.name;
      }
      return labelsAsRecord;
    };
    return fromPromise(getLabelsAsRecords());
  }

  get assignmentFilter(): AssignmentFilter {
    return this._assignmentFilter;
  }

  set assignmentFilter(filter: AssignmentFilter) {
    runInAction(() => {
      this._assignmentFilter = filter;
      this.clearSelectedChatsList();
    });
  }

  get statusFilter(): ConversationStatus | undefined {
    return this._statusFilter;
  }

  set statusFilter(status: ConversationStatus | undefined) {
    runInAction(() => {
      this._statusFilter = status;
      this.clearSelectedChatsList();
    });
  }

  async getAssignableAgents(phoneId: string): Promise<AssignableAgent[]> {
    if (this._assignableAgentsByPhone.has(phoneId)) {
      return this._assignableAgentsByPhone.get(phoneId) ?? [];
    }
    try {
      const res = await this.dataStore.wsClient.send(MessagingCommands.GET_ASSIGNABLE_AGENTS, { phoneId });
      runInAction(() => {
        this._assignableAgentsByPhone.set(phoneId, res.agents);
      });
      return res.agents;
    } catch (err) {
      this.logger.error({ err }, "failed to get assignable agents");
      return [];
    }
  }

  async updateConversationStatus(chatId: string, status: ConversationStatus) {
    try {
      const res = await this.dataStore.wsClient.send(MessagingCommands.UPDATE_CONVERSATION_STATUS, { chatId, status });
      if (res.success) {
        modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.status.update_success") });
      } else {
        modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.status.update_failed"), type: "error" });
      }
    } catch (err) {
      this.logger.error({ err }, "failed to update chat status");
      modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.status.update_failed"), type: "error" });
    }
  }

  async assignAgentsToConversation(chatId: string, addAgentIds: string[], removeAgentIds: string[]) {
    try {
      const res = await this.dataStore.wsClient.send(MessagingCommands.ASSIGN_AGENTS_TO_CONVERSATION, {
        chatId,
        addAgentIds,
        removeAgentIds,
      });
      if (res.success) {
        modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.assign_agent.update_success") });
      } else {
        modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.assign_agent.update_failed"), type: "error" });
      }
    } catch (err) {
      this.logger.error({ err }, "failed to assign agents to chat");
      modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.assign_agent.update_failed"), type: "error" });
    }
  }
}
